// src/pages/CreateGroup.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../store';
import { groupsAPI } from '../utils/api';
import { getPlatform } from '../utils/platforms';

export default function CreateGroup() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const { accounts, fetchAccounts, createGroup } = useStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAccIds, setSelectedAccIds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchAccounts();
    if (isEdit) {
      groupsAPI.get(id).then(r => {
        setName(r.data.group.name);
        setDescription(r.data.group.description || '');
        setSelectedAccIds(r.data.accounts.map(a => a.id));
      }).catch(() => toast.error('Failed to load group'));
    }
  }, [id]);

  const filteredAccounts = accounts.filter(a => a.account_name.toLowerCase().includes(search.toLowerCase()));

  const handleSave = async () => {
    if (!name.trim()) { toast.error('Group name is required'); return; }
    setLoading(true);
    try {
      if (isEdit) {
        await groupsAPI.update(id, { name, description, account_ids: selectedAccIds });
        toast.success('Group updated!');
      } else {
        await createGroup({ name, description, account_ids: selectedAccIds });
        toast.success(`Group "${name}" created!`);
      }
      navigate('/groups');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save group');
    } finally { setLoading(false); }
  };

  return (
    <div style={{ overflowY: 'auto', height: '100%' }}>
      <div style={{ padding: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: '#0f766e', cursor: 'pointer', marginBottom: 10 }} onClick={() => navigate('/groups')}>← Groups</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <span style={{ fontSize: 15, fontWeight: 600, color: '#1a1f2e', borderBottom: '2px solid #f4a118', paddingBottom: 6, display: 'inline-block' }}>{isEdit ? 'Edit Group' : 'Create Group'}</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-outline" onClick={() => navigate('/groups')}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
          </div>
        </div>

        {/* Group Details */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e', borderBottom: '2px solid #f4a118', paddingBottom: 6, display: 'inline-block', margin: '14px 14px 14px' }}>Group Details</div>
          <div style={{ padding: '0 14px 14px' }}>
            <div className="form-group">
              <label className="form-label">Group Name <span className="req">*</span></label>
              <input className="form-input" placeholder="Enter group name" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label className="form-label">Group Description</label>
              <textarea className="form-textarea" rows={3} placeholder="Optional description…" value={description} onChange={e => setDescription(e.target.value)} />
            </div>
          </div>
        </div>

        {/* Accounts selection */}
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1f2e', borderBottom: '2px solid #f4a118', paddingBottom: 6, display: 'inline-block', margin: '14px 14px 14px' }}>Accounts</div>
          <div style={{ padding: '0 14px 14px' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div className="search-box" style={{ flex: 1 }}><span style={{ color: '#aaa' }}>🔍</span><input placeholder="Search an account" value={search} onChange={e => setSearch(e.target.value)} style={{ width: '100%' }} /></div>
              <button className="filter-pill">Sort by - A to Z ▾</button>
              <button className="filter-pill">⚡ Filter Accounts</button>
            </div>
            {selectedAccIds.length > 0 && (
              <div style={{ marginBottom: 8, fontSize: 11, color: '#0f766e', fontWeight: 600 }}>
                ✓ {selectedAccIds.length} account(s) selected
                <span style={{ cursor: 'pointer', marginLeft: 10, color: '#8892a4', fontWeight: 400 }} onClick={() => setSelectedAccIds([])}>Clear all</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
              {filteredAccounts.map(acc => {
                const p = getPlatform(acc.platform);
                const sel = selectedAccIds.includes(acc.id);
                return (
                  <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', border: `1px solid ${sel ? p.color : '#e8eaf0'}`, borderRadius: 6, cursor: 'pointer', fontSize: 11, color: acc.is_connected ? '#1a1f2e' : '#e55b2d', background: sel ? p.bg : '#fff', transition: 'all .15s' }}
                    onClick={() => setSelectedAccIds(prev => prev.includes(acc.id) ? prev.filter(x => x !== acc.id) : [...prev, acc.id])}>
                    <input type="checkbox" style={{ accentColor: p.color, flexShrink: 0 }} checked={sel} onChange={() => {}} />
                    <div className="platform-icon" style={{ background: p.bg, color: p.color, width: 16, height: 16, fontSize: 8, flexShrink: 0 }}>{p.icon}</div>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.account_name}</span>
                    {!acc.is_connected && <span style={{ marginLeft: 'auto', fontSize: 11, flexShrink: 0 }}>↺</span>}
                  </div>
                );
              })}
              {filteredAccounts.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: 20, color: '#aaa', fontSize: 11 }}>No accounts found</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
